import { Component, Element, Host, Prop, h } from '@stencil/core';
import warn from '../../../capabilities/warn';
import oneCheckboxValidator from './_one-checkbox-validator';
import uid from '../../../capabilities/identity/uid';

/**
 * @description A fieldset for a set of components, especially useful for sets
 * of checkboxes or radio buttons.  Expects a legend and a set of form controls
 *
 * @use
 * @since 6.0.0
 * @slot - `default slot`: The insertion of the fieldset element.
 */
@Component({
  tag: 'hp6-fieldset',
  shadow: false, // Must be shadow:false like all top-level form components.
})
export class Fieldset {
  /**
   * The help text to display.
   *
   * @see helpText
   */
  @Prop() helpText: string;

  /**
   * Legend size
   */
  @Prop() legendSizeSelection: 'xs' | 'sm' | 'md' | 'lg';

  /**
   * Legend font type
   */
  @Prop() legendTypeSelection: 'heading' | 'body';

  /**
   * Choose a pre-provided custom validator function to use, if any.
   */
  @Prop() useValidator: 'one-checkbox-required' | 'default' = 'default';

  /**
   * A string attribute containing the error essage to display
   */
  @Prop() validatorErrorMessage: string;

  /**
   * A string attribute containing the success message to display
   */
  @Prop() validatorSuccessMessage: string;

  /**
   * Validate the upon focus leaving the fieldset
   */
  @Prop() validateOnBlur: boolean = true;

  /**
   * Validate when the fieldset is first loaded
   */
  @Prop() validateOnLoad: boolean = false;

  @Element() host: HTMLElement;

  get fieldsetElement(): HTMLFieldSetElement {
    const el = this.host.querySelector('fieldset');
    if (!el) {
      warn('fieldset-decorator: no fieldset found');
    }
    return el;
  }

  componentWillLoad() {
    const { fieldsetElement } = this;
    fieldsetElement.id = fieldsetElement.id || `fieldset-${uid()}`;
  }

  /**
   * render()
   *
   * @returns tsx
   */
  render() {
    const {
      fieldsetElement,
      legendSizeSelection,
      legendTypeSelection,
      validatorErrorMessage,
      validatorSuccessMessage,
      validateOnBlur,
      validateOnLoad,
      useValidator,
      helpText,
    } = this;

    if (!fieldsetElement) {
      warn('fieldset render(): fieldset element not found in slot');
      return null;
    }

    let validatorProp = {};
    if (useValidator === 'one-checkbox-required') {
      validatorProp = {
        validator: oneCheckboxValidator,
      };
    }

    return (
      <Host>
        <hp6-inline-validator
          error-message={validatorErrorMessage}
          success-message={validatorSuccessMessage}
          validate-on-blur={validateOnBlur}
          validate-on-load={validateOnLoad}
          {...validatorProp}
        >
          <hp6-fieldset-decorator
            legend-size-selection={legendSizeSelection}
            legend-type-selection={legendTypeSelection}
          >
            <slot />
          </hp6-fieldset-decorator>
        </hp6-inline-validator>
        {helpText ? <hp6-help-text help-text={helpText} htmlFor={fieldsetElement.id} /> : null}
      </Host>
    );
  }
}
